import React, { FC, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { useFocusRing } from '@react-aria/focus';
import { WarningSolidIcon } from '@stpkg/stand-react';

import styles from './index.module.css';

export interface RSStepHeaderItemProps {
  itemKey: string;
  title: string;
  isActive?: boolean;
  isError?: boolean;
  onClick: (itemKey: string) => void;
}

export const RSStepHeaderItem: FC<RSStepHeaderItemProps> = ({
  itemKey,
  title,
  isActive,
  isError,
  onClick,
}) => {
  const { isFocusVisible, focusProps } = useFocusRing();

  const color = '#0066FF';

  const textColorClasses = useMemo(() => {
    if (isActive) {
      return ['text-white'];
    }

    if (isError) {
      return ['text-red-700'];
    }

    return ['text-gray-1000'];
  }, [isActive, isError]);

  const bgColorClasses = useMemo(() => {
    if (isActive) {
      if (!isError) {
        return [];
      } else {
        return ['bg-red-700'];
      }
    }

    return ['bg-transparent', 'hover:bg-gray-50'];
  }, [isActive, isError]);

  const iconFillClasses = useMemo(() => {
    if (!isActive) {
      return ['fill-red-700'];
    }

    return ['fill-white'];
  }, [isActive]);

  const onButtonClick = useCallback(() => {
    onClick(itemKey);
  }, [itemKey, onClick]);

  return (
    <>
      <div
        className={classnames(
          'wrapper',
          isFocusVisible && 'focus-visible',
          'relative',
          'inline-block',
          'align-middle',
          'after:transition',
          styles['wrapper'],
        )}
      >
        <button
          {...focusProps}
          className={classnames(
            'rs-font',
            'button',
            isActive && 'active',
            isError && 'error',
            'block',
            'relative',
            'box-border',
            'px-2',
            'py-1',
            bgColorClasses,
            textColorClasses,
            'text-13',
            'pc:text-12',
            'font-normal',
            'border-none',
            'rounded-full',
            'outline-none',
            'focus:outline-none',
            'cursor-pointer',
            'z-1',
            'transition',
            styles['button'],
            isError && styles['button--error'],
          )}
          onClick={onButtonClick}
        >
          <div
            className={classnames(
              'flex',
              'items-center',
              'before:block',
              styles['content'],
              isError && 'before:content-none',
            )}
          >
            {isError && (
              <div className="mr-[2px]">
                <WarningSolidIcon
                  width="16"
                  height="16"
                  viewBox="0 0 48 48"
                  className={classnames('block', iconFillClasses, 'transition')}
                />
              </div>
            )}
            {title}
          </div>
        </button>
      </div>
      <style jsx>{`
        .wrapper::after {
          content: '';
          box-sizing: border-box;
          position: absolute;
          width: calc(100% + 8px);
          height: calc(100% + 8px);
          top: -4px;
          left: -4px;
          border-style: solid;
          border-width: 2px;
          border-color: transparent;
          border-radius: 999px;
        }

        .wrapper.focus-visible::after {
          border-color: ${color};
        }

        .button.active:not(.error) {
          background-color: ${color};
        }
      `}</style>
    </>
  );
};
